import { authedFetch } from "../api/authedFetch";

/**
 * @cobuntu/widgets — the canonical answer to "is this visitor signed in, and
 * who are they?" (app platform S3).
 *
 * ── The problem this solves ─────────────────────────────────────────────
 *
 * Every widget used to answer that by reading `cobuntu_auth` with
 * `document.cookie` and decoding the JWT in the browser. S5 makes that cookie
 * **httpOnly**, so a script injected into a community page cannot steal a
 * member's session — and on that day `document.cookie` returns nothing, every
 * widget decides the visitor is a guest, and belaescala.com, w35.org and
 * dddimo.com all silently render signed-out UI to signed-in people. Nothing
 * throws. That is why S3 blocks S5.
 *
 * ── Why there is no token exchange here ─────────────────────────────────
 *
 * The plan called this an "access-token exchange", on the assumption the
 * widget needed a token in hand. It does not. Widgets already call the API
 * through a **same-origin** path (`/api/...` on the customer's own apex), and
 * `credentials: "include"` means the browser attaches the cookie itself. The
 * apex proxy reads it server-side and forwards it as Bearer.
 *
 * httpOnly does not affect any of that — it blocks JS from READING a cookie,
 * not the browser from SENDING it. So the fetch path was never the thing that
 * breaks. The only thing that breaks is the client-side decode, and the fix is
 * to stop decoding: ask the server who you are instead.
 *
 * That is strictly better than minting a short-lived token for the widget. No
 * new token type, no second secret, and the session value never becomes
 * reachable from JavaScript in any form — which is the entire point of S5.
 *
 * ── Why it is cached and de-duplicated ──────────────────────────────────
 *
 * Reading a cookie was free and synchronous. This is a network call, and a
 * page can mount five widgets at once (nav, messages, notifications, menu,
 * cart), each of which used to ask independently. Without the shared promise
 * that is five identical requests on every page load, on a customer's own
 * domain. `getSession()` returns the SAME in-flight promise to every caller
 * and caches the result for the page's lifetime.
 */

export interface WidgetSessionUser {
  /** The user's id. `/me` has used both `id` and `userId`; either is read. */
  userId: string | null;
  name: string | null;
  usertag: string | null;
  profileImage: string | null;
  email: string | null;
}

export interface WidgetSession {
  authenticated: boolean;
  user: WidgetSessionUser | null;
}

const GUEST: WidgetSession = { authenticated: false, user: null };

/**
 * Cached result for the page's lifetime, plus the in-flight promise so
 * concurrent callers share one request rather than racing five.
 */
let cached: WidgetSession | null = null;
let inFlight: Promise<WidgetSession> | null = null;

/**
 * Resolve the current session by asking the server.
 *
 * A 401 is the ordinary "not signed in" answer, not an error — the widget
 * renders guest UI and that is a correct outcome, so it must not surface as a
 * failure.
 *
 * A network failure also resolves to guest rather than rejecting. Every caller
 * is deciding what to paint; a rejected promise would give each of them an
 * error path to forget, and a forgotten one renders nothing at all. Guest is
 * the safe wrong answer: a signed-in member briefly sees a Sign in link, which
 * is recoverable, where a thrown error leaves a blank widget.
 */
async function fetchSession(): Promise<WidgetSession> {
  try {
    // No authToken passed: the cookie rides the same-origin request on its
    // own, which is exactly what keeps working once it is httpOnly.
    const res = await authedFetch("/api/users/me");
    if (!res.ok) return GUEST;

    const body = (await res.json()) as
      | { user?: Record<string, unknown> }
      | Record<string, unknown>;

    // The BE has returned both a bare user object and a `{ user }` wrapper on
    // this endpoint depending on version. Unwrap defensively rather than
    // guessing — reading the wrong shape yields a signed-in visitor with no
    // name, which renders as a nameless account menu instead of an error.
    const envelope = body as { user?: Record<string, unknown> } | Record<string, unknown>;
    const raw = ((envelope as { user?: Record<string, unknown> }).user ??
      envelope) as Record<string, unknown> | null;
    if (!raw || typeof raw !== "object") return GUEST;

    const str = (v: unknown): string | null => (typeof v === "string" && v ? v : null);

    return {
      authenticated: true,
      user: {
        userId: str(raw.id) ?? str(raw.userId),
        name: str(raw.name),
        usertag: str(raw.usertag),
        profileImage: str(raw.profileImage),
        email: str(raw.email),
      },
    };
  } catch {
    return GUEST;
  }
}

/**
 * The session for this page. Shared across every widget; resolves once.
 */
export function getSession(): Promise<WidgetSession> {
  if (cached) return Promise.resolve(cached);
  if (inFlight) return inFlight;

  inFlight = fetchSession().then((s) => {
    cached = s;
    inFlight = null;
    return s;
  });
  return inFlight;
}

/**
 * Convenience for the common "should I render authed UI?" check.
 */
export async function isAuthenticated(): Promise<boolean> {
  return (await getSession()).authenticated;
}

/**
 * Drop the cached session.
 *
 * Call after anything that changes who the visitor is — sign-out, or a
 * sign-in completing in the same page. Without this a widget keeps painting
 * the previous visitor until a full reload, which after a sign-OUT means
 * showing the last person's name.
 */
export function resetSession(): void {
  cached = null;
  inFlight = null;
}

/**
 * The already-resolved session, or null if it has not been fetched yet.
 *
 * This exists so migrating the widgets is a small change rather than a
 * dangerous one. Every widget's render logic asks "is this visitor signed in?"
 * SYNCHRONOUSLY, in several places each. Making all of those async would mean
 * restructuring the render flow of five bundles that run on three live
 * customer sites — a lot of surface for a change whose only goal is to stop
 * reading a cookie.
 *
 * Instead a widget awaits `primeSession()` ONCE at mount, then keeps using its
 * existing synchronous checks against this cache. Same control flow, same
 * render order, one await at the entry point.
 *
 * Returns null when the session is not yet known. Callers should treat that as
 * "not signed in yet" rather than "guest forever" — which is what priming at
 * mount guarantees they never actually see.
 */
export function getCachedSession(): WidgetSession | null {
  return cached;
}

/**
 * Resolve the session and populate the cache, so later synchronous reads have
 * an answer. Await this once during a widget's mount, before first render.
 */
export async function primeSession(): Promise<WidgetSession> {
  return getSession();
}

/** Test seam: read the cache without triggering a fetch. */
export function __peekCachedSession(): WidgetSession | null {
  return cached;
}
